import React, { useState, useRef, useEffect } from "react"
import {
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  TextField
} from "@material-ui/core"

const SEARCH_DEBOUNCE_MS = 300

/**
 * A dropdown menu that is separated from the MobX state.
 *
 * When an `onSearch` callback is supplied the dropdown queries the server as the
 * user types (debounced) and renders `items` as-is — the server is responsible
 * for filtering. Without `onSearch` it falls back to client-side substring
 * filtering of the pre-loaded `items`.
 */
const DropdownMenu = ({
  selected, type, icon, setSelected, items, onSearch, hasMore, onLoadMore, label = 'Select Org', className = 'menu capitalized'
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  // const [isOpen, setOpen] = useState(false)
  const [filter, setFilter] = useState()
  const debounceRef = useRef(null)

  // Cancel any pending debounced search so it can't fire after the dropdown
  // closes (or unmounts) and mutate the shared org list while the menu is shut.
  const cancelPendingSearch = () => clearTimeout(debounceRef.current)
  useEffect(() => cancelPendingSearch, [])

  const handleToggle = (event) => {
    const opening = !anchorEl
    if (!opening) cancelPendingSearch()
    setAnchorEl(anchorEl ? null : event.currentTarget)
    // In server-search mode, reset to the full first page on open so a prior
    // query's narrowed results don't persist when the dropdown is reopened.
    if (opening && onSearch) {
      setFilter(null)
      onSearch('')
    }
  }

  const handleSelection = (event, name) => {
    if (name) setSelected(name)
    if (event && anchorEl && anchorEl.contains(event.target))
      return
    cancelPendingSearch()
    setAnchorEl(null)
    setFilter(null)
  }

  const handleFilter = (e) => {
    const value = e.target.value
    setFilter(value)
    if (onSearch) {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => onSearch(value), SEARCH_DEBOUNCE_MS)
    }
  }

  // Load the next page without closing the dropdown or clearing the search box.
  const handleLoadMore = (e) => {
    if (e) e.stopPropagation()
    if (onLoadMore) onLoadMore()
  }

  const isOpen = Boolean(anchorEl);

  let filledText = selected ? selected : label
  if (type) filledText = `${type}: ${filledText}` 
  return (
    <div className="dropdown_menu">
      <Button 
        aria-owns={isOpen ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        style={{ textTransform: 'none' }}
      >
        {<div className='label'>{filledText}</div>}
        {icon ? <img src={icon}/> : null}
      </Button>
      <Popper open={isOpen} anchorEl={anchorEl} transition disablePortal placement={'bottom-end'} style={{ width: 400, zIndex: '1' }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper className={className}>
              <ClickAwayListener onClickAway={(e) => handleSelection(e)}>              
                <MenuList>
                  <MenuItem
                    style={{ backgroundColor: 'transparent' }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                      <TextField size="small" autoFocus onChange={handleFilter} label={label} variant="outlined" fullWidth />
                  </MenuItem>
                  {(onSearch ? items : items.filter((i) => filter ? i?.toLowerCase().indexOf(filter.toLowerCase()) > -1 : true)).map((name,i) => (
                    <MenuItem key={i} onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => handleSelection(e, name, i)}>
                      {name}
                    </MenuItem>
                  ))}
                  {hasMore && onLoadMore ? (
                    <MenuItem
                      key="load-more"
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={handleLoadMore}
                    >
                      <em>Load more…</em>
                    </MenuItem>
                  ) : null}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

export default DropdownMenu
